import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-hot-toast';
import { CustomSelect, DialogShell, DialogHeader, DialogFooter, dialogButtonClass, FormFieldWithError, LoadingButton, Textarea } from '@redpanda/ui';
import { startPeriodClosing } from '../services/api';
import { AccountingPeriod, ClosingType } from '../stores/periodStore';
import { apiErrorMessage } from '../utils/apiError';

interface PeriodClosingDialogProps {
  period: AccountingPeriod;
  /** 這個期間是會計年度最後一期：可以選年結 */
  isLastPeriod: boolean;
  onClose: () => void;
  onClosed: () => void;
}

/**
 * 結帳：選類型（月結；年度最後一期可年結：結轉損益並關閉整個年度）＋備註
 * 後端會先檢查試算表平衡；結帳後這個期間的分錄不能新增、修改、過帳
 */
const PeriodClosingDialog: React.FC<PeriodClosingDialogProps> = ({ period, isLastPeriod, onClose, onClosed }) => {
  const { t } = useTranslation();
  const [type, setType] = useState<ClosingType>('MONTHLY');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const types: ClosingType[] = isLastPeriod ? ['MONTHLY', 'QUARTERLY', 'YEARLY'] : ['MONTHLY', 'QUARTERLY'];

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setSaving(true);
    try {
      await startPeriodClosing({ periodId: period.id, type, notes: notes.trim() || undefined });
      toast.success(t('periods.toast.closingCompleted', { name: period.name }));
      onClosed();
      onClose();
    } catch (error) {
      toast.error(apiErrorMessage(error, 'periods.toast.closingError'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <DialogShell onClose={onClose} closeDisabled={saving} className="max-w-md">
      <DialogHeader title={t('periods.closingDialog.title', { name: period.name })} onClose={onClose} closeDisabled={saving} closeLabel={t('common.actions.close')} />
      <form onSubmit={submit}>
        <div className="p-6 space-y-5">
          <p className="text-sm text-fg-secondary">{t('periods.closingDialog.description')}</p>
          <FormFieldWithError label={t('periods.closingDialog.type')} required htmlFor="closingType" helpText={type === 'YEARLY' ? t('periods.closingDialog.yearlyHint') : undefined}>
            <CustomSelect
              id="closingType"
              value={type}
              onChange={value => setType(value as ClosingType)}
              options={types.map(value => ({ value, label: t(`periods.closingTypes.${value}`) }))}
            />
          </FormFieldWithError>
          <FormFieldWithError label={t('periods.closingDialog.notes')} htmlFor="closingNotes">
            <Textarea id="closingNotes" rows={3} maxLength={1000} value={notes} onChange={event => setNotes(event.target.value)} />
          </FormFieldWithError>
        </div>
        <DialogFooter>
          <button type="button" onClick={onClose} disabled={saving} className={dialogButtonClass.cancel}>{t('common.actions.cancel')}</button>
          <LoadingButton type="submit" loading={saving} className={dialogButtonClass.primary}>{t('periods.actions.close')}</LoadingButton>
        </DialogFooter>
      </form>
    </DialogShell>
  );
};

export default PeriodClosingDialog;
